import { format } from 'date-fns';

export function isFutureDate(dateStr: string | null) {
  const today = new Date();
  if (dateStr !== null) {
    dateStr += ' 12:00:00';
    const date = new Date(dateStr);

    if (date <= today) {
      return false;
    } else {
      return true;
    }
  } else return false;
}

export function formatDateWithoutTime(date: Date | null) {
  if (date === null) {
    return null;
  }

  return format(date, 'yyyy-MM-dd');
}

export function getUtcDate(date?: Date): Date {
  // A JS Date is an absolute instant; moment(...).utc().toDate() returned that
  // same instant, so this is a behavior-preserving clone (and avoids aliasing).
  return new Date(date ?? new Date());
}

export function getFormattedDate(date: Date, formatStr: string): string {
  return format(date, formatStr);
}

export function getLocalFormattedDateFromUtc(utcDate: Date, formatStr: string): string {
  // utcDate is a Date instant; date-fns format renders it in the local timezone,
  // matching the previous moment.utc(...).local().format(...) behavior.
  return format(utcDate, formatStr);
}

/**
 * Parses a server `*OnUtc` timestamp as the UTC instant it represents.
 *
 * The APIs project these from plain .NET `DateTime` columns (SQL `datetime2`), which EF materializes
 * as `DateTimeKind.Unspecified` — so System.Text.Json emits `2026-08-31T14:52:00` with **no** `Z` and
 * no offset. `new Date()` parses a date-time string in that form as LOCAL time (ECMA-262), which
 * silently reinterprets a UTC instant as a local one: the reader sees UTC clock digits labelled as
 * their own time, off by the whole zone offset.
 *
 * Appending the designator is a no-op for values that already carry one (`Z` or `±HH:MM`), so this is
 * safe against an API that later starts serializing `DateTimeOffset`.
 */
export function parseUtcDateTime(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  // Anchored at the end so the `-` in the date part cannot be mistaken for a negative offset.
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasTimeZone ? value : `${value}Z`);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parses a plain calendar-date string ('yyyy-MM-dd') as a LOCAL date.
 *
 * `new Date('2022-03-14')` parses as UTC midnight, which renders as the previous
 * day in negative-offset timezones. These values are deliberately date-only (no
 * time/UTC), so we build a local Date from the date parts to avoid that shift.
 */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Formats a plain calendar-date string as e.g. "Mar 14, 2022", or null when absent/invalid. */
export function formatDateOnly(value: string | null | undefined): string | null {
  const date = parseDateOnly(value);
  if (!date) {
    return null;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Formats a diagnosed date for display. When the date is flagged approximate, only the
 * year is shown as "Approx. 2015"; otherwise it renders as "Mar 14, 2022".
 */
export function formatDiagnosedDate(value: string | null | undefined, isApproximate: boolean): string | null {
  if (!value) {
    return null;
  }

  if (isApproximate) {
    const match = /^(\d{4})/.exec(value);
    return match ? `Approx. ${match[1]}` : null;
  }

  return formatDateOnly(value);
}
